import { type GetGlobalDataFreshnessResponse } from '@autone/redux/data-ingestion';
import { useAutoneTranslation } from '@autone/translations';
import { useIsMobile } from '@autone/utils';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { type SerializedError } from '@reduxjs/toolkit/react';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
} from './components/Sidebar';
import { SidebarGroup } from './components/SidebarGroup';
import { SidebarAutoneLogo } from './components/SidebarLogo';
import { useSidebarContext } from './context/SidebarContext';
import { DataFreshnessWidget } from './data-freshness-widget';
import {
  SidebarAccount,
  SidebarChatButton,
  SidebarHub,
  SidebarModules,
  SidebarUserCard,
} from './features';
import { LanguageSelector } from './language-selector';
import {
  type SideBarConfig,
  type SidebarNavigationItemProps,
} from './types/Sidebar.types';

interface AppSidebarProps {
  config: {
    hub: SideBarConfig;
    modules: SideBarConfig;
    account: SideBarConfig;
  };
  userCardConfig: SidebarNavigationItemProps[];
  profile: {
    name: string;
    surname: string;
    imageUrl?: string;
    email?: string;
  };
  globalDataFreshness: {
    data?: GetGlobalDataFreshnessResponse;
    isLoading: boolean;
    isFetching: boolean;
    error?: FetchBaseQueryError | SerializedError;
  };
}

const LanguageSelectorWithContext = () => {
  const { common } = useAutoneTranslation();
  const { state } = useSidebarContext();
  const isMobile = useIsMobile();

  return (
    <LanguageSelector
      isCollapse={state === 'collapsed' && !isMobile}
      languages={['en', 'en-US', 'fr', 'it']}
      labelDict={{
        'en-US': common('language.label.english-us'),
        en: common('language.label.english'),
        it: common('language.label.italian'),
        fr: common('language.label.french'),
      }}
    />
  );
};

export const AppSidebar = ({
  config,
  userCardConfig,
  profile,
  globalDataFreshness,
}: AppSidebarProps) => {
  const { name, surname, imageUrl, email } = profile;
  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarTrigger id="sidebar trigger" aria-label="sidebar trigger" />
        <SidebarContent>
          <SidebarGroup>
            <SidebarAutoneLogo />
            <SidebarHub config={config.hub} />
            <SidebarModules config={config.modules} />
            <SidebarSeparator />
            <SidebarAccount config={config.account} />
            <SidebarRail />
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter className="gap-0">
          <DataFreshnessWidget
            isLoading={globalDataFreshness.isLoading}
            isFetching={globalDataFreshness.isFetching}
            data={globalDataFreshness.data}
            error={globalDataFreshness.error}
          />
          <SidebarSeparator />
          <div className="flex flex-col gap-1.5">
            <SidebarChatButton />
            <LanguageSelectorWithContext />
            <SidebarUserCard
              name={name}
              surname={surname}
              imageUrl={imageUrl}
              email={email}
              userOptionsConfig={userCardConfig}
            />
          </div>
        </SidebarFooter>
      </Sidebar>
    </SidebarProvider>
  );
};
